// src/pages/business/mpesa/MpesaDashboard.tsx
// Exact replica of mobile MpesaTransactionsScreen.tsx
import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, CheckCircle, Clock, XCircle, HelpCircle, Receipt, Loader2, Settings } from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import apiClient from '@/services/apiClient';
import BusinessPageLayout from '@/components/business/BusinessPageLayout';
import { useToast } from '@/contexts/ToastContext';

interface Transaction {
  id: string;
  status: string;
  amount?: string | number;
  created_at: string;
  phone_number?: string;
  result_description?: string;
  mpesa_receipt_number?: string;
}

interface Stats { total: number; completed: number; pending: number; failed: number; totalAmount: number; }

const statusColor = (s: string) =>
  s === 'completed' ? '#10B981' : s === 'pending' ? '#F59E0B' : s === 'failed' ? '#EF4444' : '#6B7280';

const StatusIcon: React.FC<{ status: string; size?: number }> = ({ status, size = 16 }) => {
  const color = statusColor(status);
  if (status === 'completed') return <CheckCircle size={size} color={color} />;
  if (status === 'pending')   return <Clock size={size} color={color} />;
  if (status === 'failed')    return <XCircle size={size} color={color} />;
  return <HelpCircle size={size} color={color} />;
};

const formatPhone = (p?: string) => p ? p.replace('254', '0') : 'N/A';
const formatAmount = (a?: string | number) => `KSh ${Number(a || 0).toLocaleString()}`;

// ── Stats Card ────────────────────────────────────────────────────────────────
const StatCard: React.FC<{ title: string; value: React.ReactNode; subtitle?: string; color?: string }> = ({ title, value, subtitle, color }) => (
  // statCard: flex1, minWidth 45%, padding 16, borderRadius 12, alignItems center
  <div className="flex-1 min-w-[45%] flex flex-col items-center p-4 rounded-xl bg-light-card dark:bg-dark-card" style={{ marginRight: 12, marginBottom: 12 }}>
    <p className="font-bold mb-1" style={{ fontSize: 20, color: color || '#0F172A' }}>{value}</p>
    <p className="font-semibold text-center" style={{ fontSize: 14, color: '#0F172A' }}>{title}</p>
    {subtitle && <p className="text-center" style={{ fontSize: 12, color: '#64748B' }}>{subtitle}</p>}
  </div>
);

// ── Transaction Item ──────────────────────────────────────────────────────────
const TransactionItem: React.FC<{ item: Transaction }> = ({ item }) => {
  const sc = statusColor(item.status);
  return (
    // transactionItem: row, justifyContent space-between, padding 16, borderRadius 12, mb 8
    <div className="flex items-center justify-between p-4 rounded-xl bg-light-card dark:bg-dark-card mb-2">
      {/* left */}
      <div className="flex items-center gap-3 flex-1">
        {/* statusIcon: w32 h32, borderRadius 16 */}
        <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: sc }}>
          <StatusIcon status={item.status} size={16} />
        </div>
        <div className="flex-1 min-w-0">
          {/* phoneNumber: fontSize 16, fontWeight 600 */}
          <p className="font-semibold" style={{ fontSize: 16, color: '#0F172A' }}>{formatPhone(item.phone_number)}</p>
          {/* transactionDesc: fontSize 14 */}
          <p className="truncate" style={{ fontSize: 14, color: '#64748B' }}>{item.result_description || 'Payment request sent'}</p>
          {/* transactionTime: fontSize 12 */}
          <p style={{ fontSize: 12, color: '#64748B' }}>{new Date(item.created_at).toLocaleTimeString()}</p>
        </div>
      </div>
      {/* right */}
      <div className="flex flex-col items-end gap-1">
        {/* amount: fontSize 16, fontWeight bold */}
        <p className="font-bold" style={{ fontSize: 16, color: '#0F172A' }}>{formatAmount(item.amount)}</p>
        {item.mpesa_receipt_number && (
          <p style={{ fontSize: 12, color: '#64748B' }}>{item.mpesa_receipt_number}</p>
        )}
        {/* statusBadge: px 8, py 4, borderRadius 12 */}
        <span className="px-2 py-1 rounded-xl text-white text-[10px] font-semibold" style={{ backgroundColor: sc }}>
          {item.status?.charAt(0).toUpperCase() + item.status?.slice(1)}
        </span>
      </div>
    </div>
  );
};

// ── Screen ────────────────────────────────────────────────────────────────────
export const MpesaDashboard: React.FC = () => {
  const { businessId } = useParams<{ businessId: string }>();
  const navigate = useNavigate();
  const { error: toastError } = useToast();

  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [transactions, setTransactions] = useState<Transaction[]>([]);
  const [stats, setStats] = useState<Stats>({ total: 0, completed: 0, pending: 0, failed: 0, totalAmount: 0 });

  const loadTransactions = useCallback(async () => {
    try {
      setLoading(true);
      const res = await apiClient.get(`/business/${businessId}/mpesa/transactions?limit=100`);
      if (res.data.transactions) {
        const txs: Transaction[] = res.data.transactions;
        setTransactions(txs);
        const completed = txs.filter(t => t.status === 'completed').length;
        const pending   = txs.filter(t => t.status === 'pending').length;
        const failed    = txs.filter(t => t.status === 'failed').length;
        const totalAmount = txs.filter(t => t.status === 'completed').reduce((s, t) => s + Number(t.amount || 0), 0);
        setStats({ total: res.data.total || txs.length, completed, pending, failed, totalAmount });
      }
    } catch {
      toastError('Failed to load', 'Could not fetch M-Pesa transactions.');
    } finally { setLoading(false); setRefreshing(false); }
  }, [businessId]);

  useEffect(() => { loadTransactions(); }, [loadTransactions]);

  const onRefresh = () => { setRefreshing(true); loadTransactions(); };

  // Group by date
  const grouped = transactions.reduce<Record<string, Transaction[]>>((acc, t) => {
    const d = new Date(t.created_at).toLocaleDateString();
    if (!acc[d]) acc[d] = [];
    acc[d].push(t);
    return acc;
  }, {});

  const headerRight = (
    <div className="flex items-center gap-2">
      <button onClick={() => navigate(`/business/${businessId}/mpesa/setup`)} className="flex items-center gap-2 px-4 py-2 rounded-xl border border-light-border dark:border-dark-border text-sm font-bold text-light-text dark:text-dark-text hover:bg-light-bg dark:hover:bg-dark-bg transition">
        <Settings size={16} /> Setup / Configure
      </button>
      <button onClick={onRefresh} disabled={refreshing} className="p-2 rounded-xl border border-light-border dark:border-dark-border hover:bg-light-bg dark:hover:bg-dark-bg transition">
        <RefreshCw size={20} className={refreshing ? 'animate-spin' : ''} style={{ color: '#4F46E5' }} />
      </button>
    </div>
  );

  return (
    <BusinessPageLayout title="M-Pesa Transactions" headerRight={headerRight}>

      {loading ? (
        <div className="flex justify-center py-24"><Loader2 size={36} className="animate-spin" style={{ color: '#4F46E5' }} /></div>
      ) : (
        <div className="px-4 pt-4">

          {/* ── Overview stats ── */}
          {/* sectionTitle: fontSize 18, fontWeight bold, mb 16 */}
          <p className="font-bold mb-4" style={{ fontSize: 18, color: '#0F172A' }}>Overview</p>
          {/* statsGrid: row, flexWrap wrap */}
          <div className="flex flex-wrap" style={{ marginRight: -12 }}>
            <StatCard title="Total Transactions" value={stats.total}   subtitle="All time"   color="#4F46E5" />
            <StatCard title="Completed"          value={stats.completed} subtitle="Successful" color="#10B981" />
            <StatCard title="Pending"            value={stats.pending}  subtitle="Waiting"    color="#F59E0B" />
            <StatCard title="Total Amount"       value={`KSh ${stats.totalAmount.toLocaleString()}`} subtitle="Received" color="#10B981" />
          </div>

          {/* ── Transactions grouped by date ── */}
          {transactions.length === 0 ? (
            // emptyState: center, padding 40
            <div className="flex flex-col items-center justify-center py-16 gap-4 text-center">
              <Receipt size={64} style={{ color: '#94A3B8' }} />
              {/* emptyTitle: fontSize 18, fontWeight bold, mt 16 */}
              <p className="font-bold" style={{ fontSize: 18, color: '#0F172A' }}>No Transactions Yet</p>
              {/* emptySubtitle: fontSize 14, textAlign center, lineHeight 20 */}
              <p style={{ fontSize: 14, color: '#64748B', lineHeight: '20px' }}>
                Your M-Pesa transactions will appear here once customers start making payments.
              </p>
            </div>
          ) : (
            Object.entries(grouped).map(([date, txs]) => (
              <div key={date}>
                {/* dateHeader: fontSize 16, fontWeight bold, mt 16, mb 8 */}
                <p className="font-bold" style={{ fontSize: 16, color: '#0F172A', marginTop: 16, marginBottom: 8 }}>{date}</p>
                {txs.map(tx => <TransactionItem key={tx.id} item={tx} />)}
              </div>
            ))
          )}
        </div>
      )}
    </BusinessPageLayout>
  );
};

export default MpesaDashboard;
